import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Redirect, Switch } from 'react-router-dom';

import SpinnerFallback from '@common/components/spinner_fallback';
import { GlobalHistory } from './globalHistory';

const TaskSecondAside = lazy(() => import('./app/components/second_aside/task_second_aside'));
const MessengerSecondAside = lazy(() =>
  import('./app/components/second_aside/messenger_second_aside'),
);
const AppWrapper = lazy(() => import('./app/components/app_wrapper'));
const App = lazy(() => import('./app/components/app'));
const AdminPage = lazy(() => import('./pages/admin'));
const PrivacyPage = lazy(() => import('./pages/privacy'));
const TermsPage = lazy(() => import('./pages/terms'));
const RegisterPage = lazy(() => import('./pages/register'));
const SigninPage = lazy(() => import('./pages/signin'));
const ForgotPasswordPage = lazy(() => import('./pages/forgot_password'));
const PasswordResetPage = lazy(() => import('./pages/password_reset'));
const DashboardPage = lazy(() => import('./pages/dashboard'));
const SchedulePage = lazy(() => import('./pages/schedule'));
const SuppliesPage = lazy(() => import('./pages/supplies'));
const PropertyPage = lazy(() => import('./pages/property'));
const ReservationsPage = lazy(() => import('./pages/reservations'));
const WorkflowPage = lazy(() => import('./pages/workflow'));
const InvitationPage = lazy(() => import('./pages/invitation'));
const CompanySettingsPage = lazy(() => import('./pages/company_settings'));
const CompanyPage = lazy(() => import('./pages/company'));
const SettingsPage = lazy(() => import('./pages/settings'));
const ProfilePage = lazy(() => import('./pages/profile'));
const IntegrationPage = lazy(() => import('./pages/integration'));
const RepPage = lazy(() => import('./pages/rep'));
const OfferPage = lazy(() => import('./pages/offer'));
const TaskList = lazy(() => import('./pages/task_list'));
const MessagingPage = lazy(() => import('./pages/messaging'));
const TaskPage = lazy(() => import('./pages/task'));
const NotFoundPage = lazy(() => import('./pages/not_found'));
const EmailConfirmationPage = lazy(() => import('./pages/email_confirmation'));

const WRAPPERS_TO_RESET_SCROLL = ['auth-app__main-content', 'main-content', 'scroll-wrapper'];

const resetScroll = wrapperClassName => {
  const wrapper = document && _.first(document.getElementsByClassName(wrapperClassName));
  _.invoke(wrapper, 'scrollTo', 0, 0);
};

const onRootUpdate = () => {
  const { hash } = window.location;
  if (hash !== '') {
    // Push onto callback queue so it runs after the DOM is updated,
    // this is required when navigating from a different page so that
    // the element is rendered on the page before trying to getElementById.
    setTimeout(() => {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) element.scrollIntoView();
    }, 0);
  } else {
    _.forEach(WRAPPERS_TO_RESET_SCROLL, resetScroll);
  }
};

const checkUrlContainsPage = (pages, url) => RegExp(pages.join('|')).exec(url);

const getSecondAside = url => {
  const repRoutes = ['/rep', '/offer', '/maintenance', '/housekeeping', '/inspection'];
  const messaginPages = ['/messaging'];
  const isRepPage = checkUrlContainsPage(repRoutes, url);
  const isTaskList = url === '/task';
  const isMessagingPage = checkUrlContainsPage(messaginPages, url);

  if (!_.isEmpty(isRepPage) || isTaskList) {
    return TaskSecondAside;
  } else if (!_.isEmpty(isMessagingPage)) {
    return MessengerSecondAside;
  }

  return null;
};

const Root = ({ store }) => (
  <Provider store={store}>
    <Router onUpdate={onRootUpdate}>
      <GlobalHistory />

      <Route
        path="/"
        render={({ location }) => (
          <Switch>
            <Redirect exact from="/" to="/signin" />

            <Route path="/admin">
              <Suspense fallback={<SpinnerFallback />}>
                <AdminPage />
              </Suspense>
            </Route>

            <Suspense fallback={<SpinnerFallback />}>
              <AppWrapper>
                <App secondAside={getSecondAside(_.get(location, 'pathname'))}>
                  <Suspense fallback={<SpinnerFallback />}>
                    <Switch>
                      <Route path="/privacy">
                        <PrivacyPage />
                      </Route>
                      <Route path="/terms">
                        <TermsPage />
                      </Route>
                      <Route path="/register">
                        <RegisterPage />
                      </Route>
                      <Route path="/signin">
                        <SigninPage />
                      </Route>
                      <Route path="/forgot-password">
                        <ForgotPasswordPage />
                      </Route>
                      <Route path="/password-reset/:token">
                        <PasswordResetPage />
                      </Route>
                      <Route path="/dashboard">
                        <DashboardPage />
                      </Route>
                      <Route path="/schedule">
                        <SchedulePage />
                      </Route>
                      <Route path="/supplies">
                        <SuppliesPage />
                      </Route>
                      <Route path="/property">
                        <PropertyPage />
                      </Route>
                      <Route path="/reservations">
                        <ReservationsPage />
                      </Route>
                      <Route path="/workflow">
                        <WorkflowPage />
                      </Route>
                      <Route path="/invitation/:inviteCode">
                        <InvitationPage />
                      </Route>
                      <Route path="/company-settings">
                        <CompanySettingsPage />
                      </Route>
                      <Route path="/company">
                        <CompanyPage />
                      </Route>
                      <Route path="/settings">
                        <SettingsPage />
                      </Route>
                      <Route path="/profile">
                        <ProfilePage />
                      </Route>
                      <Route path="/integration/:partnerType">
                        <IntegrationPage />
                      </Route>
                      <Route path="/rep">
                        <RepPage />
                      </Route>
                      <Route path="/offer">
                        <OfferPage />
                      </Route>
                      <Route path="/task/:taskId">
                        <TaskPage />
                      </Route>
                      <Route path={['/housekeeping', '/inspection', '/maintenance', '/task']}>
                        <TaskList />
                      </Route>
                      <Route path="/messaging">
                        <MessagingPage />
                      </Route>
                      <Route path="/change-email">
                        <EmailConfirmationPage />
                      </Route>
                      <Route path="*">
                        <NotFoundPage />
                      </Route>
                    </Switch>
                  </Suspense>
                </App>
              </AppWrapper>
            </Suspense>
          </Switch>
        )}
      />
    </Router>
  </Provider>
);

Root.propTypes = {
  store: PropTypes.shape({}),
};

export default Root;
